/**
 * Tenant theme rules (brand/white-label.md). A tenant brands Teslyn with colour and logo only: it may override
 * `brand`, `on-brand`, `brand-soft`, `accent` and `on-accent`; anything else in the stored theme is ignored so QC
 * colours, surfaces, ink, type and spacing stay Teslyn's. The stored theme is `tenant.theme` (jsonb, default
 * `'{}'`); the bundled copy is `content/tenants/<slug>/theme.json`. Teslyn Cloud and Teslyn Field apply the same
 * rules (issue #43), Field once the signed-in user's organisation is known.
 */
export const OVERRIDABLE_TOKENS = ['brand', 'on-brand', 'brand-soft', 'accent', 'on-accent'] as const;
export type OverridableToken = (typeof OVERRIDABLE_TOKENS)[number];

export interface ThemeValue {
  readonly light?: string;
  readonly dark?: string;
}
export type ThemeOverrides = Partial<Record<OverridableToken, ThemeValue>>;

const COLOUR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Keep only allowed tokens with valid hex colours. Accepts the bare map, the theme file's `{ overrides: {...} }`,
 * or JSON text (PowerSync delivers a jsonb column to the laptop as text).
 */
export function sanitiseOverrides(raw: unknown): ThemeOverrides {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== 'object') return {};
  const src = (
    'overrides' in value && typeof (value as { overrides: unknown }).overrides === 'object'
      ? (value as { overrides: unknown }).overrides
      : value
  ) as Record<string, unknown> | null;
  const out: Record<string, ThemeValue> = {};
  for (const token of OVERRIDABLE_TOKENS) {
    const v = src?.[token];
    if (!v || typeof v !== 'object') continue;
    const { light, dark } = v as Record<string, unknown>;
    const entry: { light?: string; dark?: string } = {};
    if (typeof light === 'string' && COLOUR.test(light)) entry.light = light;
    if (typeof dark === 'string' && COLOUR.test(dark)) entry.dark = dark;
    if (entry.light || entry.dark) out[token] = entry;
  }
  return out as ThemeOverrides;
}

export function hasOverrides(o: ThemeOverrides): boolean {
  return Object.keys(o).length > 0;
}

/** The stored `tenant.theme` wins when it has any allowed override; otherwise the bundled copy, else nothing. */
export function chooseTheme(stored: unknown, bundled: unknown): ThemeOverrides {
  const fromStore = sanitiseOverrides(stored);
  return hasOverrides(fromStore) ? fromStore : sanitiseOverrides(bundled);
}

// ---------------------------------------------------------------------------------------------- contrast

type Mode = 'light' | 'dark';
type Surface = 'surface-000' | 'surface-100' | 'surface-200';

/** Teslyn's surfaces (`brand/tokens/tokens.json`; the test keeps the two equal). Tenants never change them. */
export const TESLYN_SURFACES: Readonly<Record<Mode, Readonly<Record<Surface, string>>>> = {
  light: { 'surface-000': '#ffffff', 'surface-100': '#f6f4f0', 'surface-200': '#ebe7e0' },
  dark: { 'surface-000': '#1c2024', 'surface-100': '#121518', 'surface-200': '#0c0e10' },
};

/** Teslyn's own words on a brand or accent fill: white in light, instrument black in dark (`on-brand`). */
export const TESLYN_ON_COLOUR: Readonly<Record<Mode, string>> = { light: '#ffffff', dark: '#121518' };

/** WCAG AA for text (brand/white-label.md). */
export const MIN_CONTRAST = 4.5;

function rgb(colour: string): [number, number, number] {
  let h = colour.replace('#', '');
  if (h.length === 3) h = [...h].map((c) => c + c).join('');
  return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

const channel = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

/** WCAG 2 relative luminance of a hex colour. */
export function relativeLuminance(colour: string): number {
  const [r, g, b] = rgb(colour).map(channel) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio of two hex colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const toHex = (c: readonly number[]): string =>
  `#${c
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/**
 * The colour, darkened (towards black, for light surfaces) or lightened (towards white, for dark ones) in 1 % steps
 * only until it reaches `min` against every surface given; unchanged when it already does. The hue stays the
 * tenant's: each step mixes the same share of black or white into all three channels.
 */
export function fitContrast(
  colour: string,
  against: readonly string[],
  min: number,
  direction: 'darken' | 'lighten',
): string {
  const ok = (c: string): boolean => against.every((s) => contrastRatio(c, s) >= min);
  const start = colour.toLowerCase();
  if (ok(start)) return start;
  const c = rgb(start);
  for (let step = 1; step <= 100; step++) {
    const t = step / 100;
    const next = toHex(c.map((v) => (direction === 'darken' ? v * (1 - t) : v + (255 - v) * t)));
    if (ok(next)) return next;
  }
  return direction === 'darken' ? '#000000' : '#ffffff';
}

/** The words on a fill: the colour preferred when it reads, else Teslyn's on-colour of either theme that reads best. */
function fitOnColour(fill: string, preferred: string, mode: Mode): string {
  const other: Mode = mode === 'light' ? 'dark' : 'light';
  const candidates = [preferred, TESLYN_ON_COLOUR[mode], TESLYN_ON_COLOUR[other]];
  const pass = candidates.find((c) => contrastRatio(c, fill) >= MIN_CONTRAST);
  if (pass) return pass.toLowerCase();
  return candidates.reduce((a, b) => (contrastRatio(b, fill) > contrastRatio(a, fill) ? b : a)).toLowerCase();
}

/**
 * The tokens a tenant theme sets after the contrast rule, with `brand-chrome` and `accent-soft` (derived, never
 * stored).
 */
export type ContrastTheme = ThemeOverrides & {
  readonly 'brand-chrome'?: ThemeValue;
  readonly 'accent-soft'?: ThemeValue;
};

/** How much of the accent goes into its soft tint over `surface-000` (R168): a wash, so `ink` reads on it. */
const ACCENT_SOFT_SHARE: Readonly<Record<Mode, number>> = { light: 0.15, dark: 0.2 };

/**
 * `accent-soft` for a tenant's accent: the accent (after the contrast rule) mixed into the theme's `surface-000`
 * at `ACCENT_SOFT_SHARE`, so the "live data on its way" banner (Space weather, R168) takes the tenant's accent as its
 * bar does. Teslyn's own `accent-soft` in `tokens.json` is set by hand to the same recipe.
 */
export function accentSoft(accent: string, mode: Mode): string {
  const share = ACCENT_SOFT_SHARE[mode];
  const base = rgb(TESLYN_SURFACES[mode]['surface-000']);
  const a = rgb(accent);
  return toHex(base.map((v, i) => v + (a[i]! - v) * share));
}

/**
 * The white-label contrast rule (brand/white-label.md; MB decisions 316 and 317, 20 Oct 2026), the same for every
 * tenant, applied when the theme is painted so the stored brand files stay as the tenant gave them:
 *
 * - `brand` and `accent` reach 4.5:1 on `surface-000` and `surface-100` in each theme (darkened in light, lightened
 *   in dark, only as far as needed).
 * - `brand-chrome` is the brand as drawn on `surface-200`, the top bar and side menu of Teslyn Field: the brand
 *   again, darkened or lightened just enough to reach 4.5:1 there. Thomson's #dd1a20 is 4.0 on the light chrome, so
 *   it is slightly darker there and unchanged everywhere else (316).
 * - `accent-soft` follows a tenant's accent (`accentSoft`): the fitted accent washed into `surface-000`, so Space
 *   weather's "Fetching latest information" banner and its bar are one colour on a tenant's portal (R168).
 * - `on-brand` and `on-accent` (the words on a brand or accent fill) keep the tenant's colour when it reaches 4.5:1
 *   on the fill; otherwise Teslyn's own on-colour for the theme, or the other one, whichever reads. Thomson's white
 *   on its dark-mode red is 3.6, so dark words are used, as Teslyn's palette does (317). An on-colour the tenant
 *   did not set is written only when Teslyn's default would not read.
 */
export function contrastTheme(o: ThemeOverrides): ContrastTheme {
  const out: Record<string, { light?: string; dark?: string }> = {};
  const put = (token: string, mode: Mode, value: string): void => {
    out[token] = { ...out[token], [mode]: value };
  };
  for (const token of OVERRIDABLE_TOKENS) {
    const v = o[token];
    if (v?.light) put(token, 'light', v.light);
    if (v?.dark) put(token, 'dark', v.dark);
  }
  for (const mode of ['light', 'dark'] as const) {
    const s = TESLYN_SURFACES[mode];
    const direction = mode === 'light' ? 'darken' : 'lighten';
    for (const [fillToken, onToken] of [
      ['brand', 'on-brand'],
      ['accent', 'on-accent'],
    ] as const) {
      const given = o[fillToken]?.[mode];
      if (!given) continue;
      const fill = fitContrast(given, [s['surface-000'], s['surface-100']], MIN_CONTRAST, direction);
      put(fillToken, mode, fill);
      if (fillToken === 'brand')
        put('brand-chrome', mode, fitContrast(fill, [s['surface-200']], MIN_CONTRAST, direction));
      if (fillToken === 'accent') put('accent-soft', mode, accentSoft(fill, mode));
      const ownOn = o[onToken]?.[mode];
      const on = fitOnColour(fill, ownOn ?? TESLYN_ON_COLOUR[mode], mode);
      if (ownOn || on !== TESLYN_ON_COLOUR[mode]) put(onToken, mode, on);
    }
  }
  return out as ContrastTheme;
}

const CSS_TOKENS = [...OVERRIDABLE_TOKENS, 'brand-chrome', 'accent-soft'] as const;

/**
 * CSS that re-declares only the overridden variables, light and dark, after the contrast rule (`contrastTheme`);
 * empty for no overrides.
 */
export function themeCss(overrides: ThemeOverrides): string {
  const o = contrastTheme(overrides);
  const decl = (mode: Mode): string =>
    CSS_TOKENS.flatMap((t) => {
      const v = o[t]?.[mode];
      return v ? [`  --${t}: ${v};`] : [];
    }).join('\n');
  const light = decl('light');
  const dark = decl('dark');
  return [light && `:root, [data-theme="light"] {\n${light}\n}`, dark && `[data-theme="dark"] {\n${dark}\n}`]
    .filter(Boolean)
    .join('\n');
}

// ---------------------------------------------------------------------------------------------- default theme

/** A theme setting a tenant may make Teslyn Field's default (decision 315): Teslyn Field's own three. */
export type TenantDefaultTheme = 'dark' | 'light' | 'system';

function defaultThemeOf(raw: unknown): TenantDefaultTheme | null {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const d = (value as { default_theme?: unknown }).default_theme;
  return d === 'dark' || d === 'light' || d === 'system' ? d : null;
}

/**
 * The theme Teslyn Field starts in for a tenant's people until each chooses their own (MB decision 315, 20 Oct
 * 2026): `default_theme` in the stored `tenant.theme`, else in the bundled `content/tenants/<slug>/theme.json`;
 * null when neither says (Teslyn Field's own default, Match Windows). The Demo tenant's file says Dark, so Demo and
 * Thomson look different at a glance.
 */
export function tenantDefaultTheme(stored: unknown, bundled: unknown): TenantDefaultTheme | null {
  return defaultThemeOf(stored) ?? defaultThemeOf(bundled);
}

/**
 * Which of the user's organisations brands Teslyn Field: the open project's, when it is one of them; else the
 * only one; else the one shown last, while the user still belongs to it; else none (Teslyn's own look). A project
 * set up offline carries the nil tenant, which matches no organisation.
 */
export function pickBrandTenant<T extends { readonly id: string }>(
  tenants: readonly T[],
  projectTenantId: string | null,
  previousId: string | null,
): T | null {
  const byId = (id: string | null): T | null => (id ? (tenants.find((t) => t.id === id) ?? null) : null);
  return byId(projectTenantId) ?? (tenants.length === 1 ? tenants[0]! : null) ?? byId(previousId);
}

/** The suite's own product title: the default of `tenant.product_title` (decision 529). */
export const DEFAULT_PRODUCT_TITLE = 'Teslyn Cloud';

/**
 * Whether a stored product title is the suite's default rather than the tenant's own. 'Survera Cloud' is the default
 * from before the rename (decision 529), still in a row until the 20261025000000 migration runs.
 */
export function isDefaultProductTitle(title: string | null | undefined): boolean {
  return !title || title === DEFAULT_PRODUCT_TITLE || title === 'Survera Cloud';
}

// ---------------------------------------------------------------------------------------------- logo size

/**
 * How tall the tenant's logo stands (MB decision 524, Q463 point 7 B): in Teslyn Field's top bar, the portal header
 * and the sign-in card. A preset (the brand tokens `--logo-tenant-sm`, `-md`, `-lg`) or a whole number of px within
 * `--logo-tenant-min` and `--logo-tenant-max`. Kept as `logo_size` in the stored `tenant.theme` (a tenant admin sets
 * it in Teslyn Cloud, Settings), else in the bundled theme file; medium (64 px, R39) when neither says. A phone shows
 * three quarters of it.
 */
export type TenantLogoSize = 'small' | 'medium' | 'large' | number;

export const TENANT_LOGO_PX = { small: 40, medium: 64, large: 88 } as const;
export const TENANT_LOGO_MIN_PX = 32;
export const TENANT_LOGO_MAX_PX = 96;
const LOGO_TOKEN = { small: 'logo-tenant-sm', medium: 'logo-tenant-md', large: 'logo-tenant-lg' } as const;

function jsonObject(raw: unknown): Record<string, unknown> | null {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Why `size` cannot be a tenant's logo size, or null when it can. */
export function checkTenantLogoSize(size: unknown): string | null {
  if (size === 'small' || size === 'medium' || size === 'large') return null;
  if (
    typeof size === 'number' &&
    Number.isInteger(size) &&
    size >= TENANT_LOGO_MIN_PX &&
    size <= TENANT_LOGO_MAX_PX
  )
    return null;
  return `Enter a height from ${TENANT_LOGO_MIN_PX} to ${TENANT_LOGO_MAX_PX} px.`;
}

function logoSizeOf(raw: unknown): TenantLogoSize | null {
  const size = jsonObject(raw)?.['logo_size'];
  return checkTenantLogoSize(size) === null ? (size as TenantLogoSize) : null;
}

/** The tenant's logo size: the stored theme's `logo_size`, else the bundled file's, else medium. */
export function tenantLogoSize(stored: unknown, bundled: unknown): TenantLogoSize {
  return logoSizeOf(stored) ?? logoSizeOf(bundled) ?? 'medium';
}

export function tenantLogoHeightPx(size: TenantLogoSize): number {
  return typeof size === 'number' ? size : TENANT_LOGO_PX[size];
}

/** The CSS that sets `--tenant-logo-h`, which the apps' logo slots read (a preset names its brand token). */
export function tenantLogoCss(size: TenantLogoSize): string {
  const value = typeof size === 'number' ? `${size}px` : `var(--${LOGO_TOKEN[size]})`;
  return `:root {\n  --tenant-logo-h: ${value};\n}`;
}

/** The stored theme with its logo size set; its colours, default theme and anything else are kept. */
export function withTenantLogoSize(theme: unknown, size: TenantLogoSize): Record<string, unknown> {
  return { ...(jsonObject(theme) ?? {}), logo_size: size };
}
